import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface UserRequest {
  id: string;
  type: string;
  status: string;
  created_at: string;
  updated_at?: string;
  brand_id: string;
  brands?: {
    id: string;
    name: string;
    description?: string;
  } | null;
  unreadCount?: number;
}

// Type for the raw data from Supabase
interface RawUserRequest {
  id: string;
  type: string;
  status: string;
  created_at: string;
  updated_at?: string;
  brand_id: string;
  brands: {
    id: string;
    name: string;
    description?: string;
  }[] | null;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  review: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Helper function to format status for display
const formatStatus = (status: string) => {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    review: 'Review',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };
  return statusMap[status] || status;
};

const RequestList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<UserRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch user requests with notification counts
  const fetchRequests = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch user's requests with brand information
      const { data: requestsData, error: requestsError } = await supabase
        .from('service_requests')
        .select(`
          id,
          type,
          status,
          created_at,
          updated_at,
          brand_id,
          brands (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (requestsError) {
        console.error('Error fetching user requests:', requestsError);
        setLoading(false);
        return;
      }

      if (requestsData) {
        // Fetch unread message counts for these requests
        const requestIds = requestsData.map(req => req.id);
        const { data: messageCounts, error: messageError } = await supabase
          .from('request_message')
          .select('request_id, sender, read')
          .in('request_id', requestIds)
          .eq('sender', 'admin') // Only count messages from team members
          .eq('read', false); // Only count unread messages

        let requestsWithNotifications: UserRequest[] = [];

        if (!messageError && messageCounts) {
          // Count messages per request
          const unreadCounts = messageCounts.reduce((acc: any, msg) => {
            acc[msg.request_id] = (acc[msg.request_id] || 0) + 1;
            return acc;
          }, {});

          // Transform raw data to UserRequest format
          requestsWithNotifications = requestsData.map((req: RawUserRequest) => {
            const brandData = req.brands && Array.isArray(req.brands) && req.brands.length > 0 ? req.brands[0] : null;
            return {
              id: req.id,
              type: req.type,
              status: req.status,
              created_at: req.created_at,
              updated_at: req.updated_at,
              brand_id: req.brand_id,
              brands: brandData,
              unreadCount: unreadCounts[req.id] || 0
            };
          });
        } else {
          // If no message counts, still transform the data
          requestsWithNotifications = requestsData.map((req: RawUserRequest) => {
            const brandData = req.brands && Array.isArray(req.brands) && req.brands.length > 0 ? req.brands[0] : null;
            return {
              id: req.id,
              type: req.type,
              status: req.status,
              created_at: req.created_at,
              updated_at: req.updated_at,
              brand_id: req.brand_id,
              brands: brandData,
              unreadCount: 0
            };
          });
        }

        setRequests(requestsWithNotifications);
      }
    } catch (error) {
      console.error('Error fetching user requests:', error);
    }
    
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Set up real-time subscription for new messages from team members
  useEffect(() => {
    if (!user) return;

    // Subscribe to new messages to update notification counts
    const messageSubscription = supabase
      .channel('request_list_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
        },
        async (payload) => {
          const newMessage = payload.new;
          
          // Only update if it's a message from a team member (admin) that's unread
          if (newMessage.sender === 'admin' && !newMessage.read) {
            console.log('🔔 New message from team member received in request list:', newMessage);
            
            // Update the unread count for this request
            setRequests(prev => prev.map(req => 
              req.id === newMessage.request_id 
                ? { ...req, unreadCount: (req.unreadCount || 0) + 1 }
                : req
            ));
          }
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, [user]);

  // Set up real-time subscription for when messages are marked as read
  useEffect(() => {
    if (!user) return;

    // Subscribe to message updates to know when they're marked as read
    const messageUpdateSubscription = supabase
      .channel('request_list_message_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'request_message',
        },
        async (payload) => {
          const updatedMessage = payload.new;
          
          // If a message was marked as read and it's from a team member
          if (updatedMessage.read && updatedMessage.sender === 'admin') {
            console.log('✅ Message marked as read in request list:', updatedMessage);
            
            // Decrease the unread count for this request
            setRequests(prev => prev.map(req => 
              req.id === updatedMessage.request_id 
                ? { ...req, unreadCount: Math.max(0, (req.unreadCount || 0) - 1) }
                : req
            ));
          }
        }
      )
      .subscribe();

    return () => {
      messageUpdateSubscription.unsubscribe();
    };
  }, [user]);

  // Filter requests based on search term
  const filteredRequests = requests.filter(req => {
    const searchLower = searchTerm.toLowerCase();
    const brandName = req.brands?.name?.toLowerCase() || '';
    const requestType = req.type?.replace(/-/g, ' ').toLowerCase() || '';
    
    return brandName.includes(searchLower) || requestType.includes(searchLower);
  });

  const handleRequestClick = (request: UserRequest) => {
    console.log('🚀 Navigation button clicked for request:', request.id);
    console.log('📍 Navigating to:', `/dashboard/requests/${request.id}`);
    
    // Try React Router navigation first
    try {
      navigate(`/dashboard/requests/${request.id}`);
    } catch (error) {
      console.error('❌ React Router navigation failed:', error);
      // Fallback to window.location
      console.log('🔄 Falling back to window.location');
      window.location.href = `/dashboard/requests/${request.id}`;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <span className="text-2xl mr-2">🔔</span>
            Your Requests & Notifications
          </h2>
          <input
            type="text"
            placeholder="Search requests..."
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      <div className="p-6">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No requests yet</h3>
            <p className="text-gray-500">Create your first design request to get started.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((req) => (
              <div
                key={req.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleRequestClick(req)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {req.type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded-full ${statusColors[req.status] || 'bg-gray-100 text-gray-800'}`}>
                        {formatStatus(req.status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      {req.brands?.name ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-900">{req.brands.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No Brand</span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600">
                      Created: {new Date(req.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {/* Notification Badge */}
                    {req.unreadCount && req.unreadCount > 0 && (
                      <div className="relative">
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">
                            {req.unreadCount > 9 ? '9+' : req.unreadCount}
                          </span>
                        </div>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                      </div>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestClick(req);
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      {req.unreadCount && req.unreadCount > 0 ? 'View Messages' : 'View Details'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestList; 